import { EventOutlined } from '@mui/icons-material'
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'
import type { PickerChangeHandlerContext } from '@mui/x-date-pickers/models'
import type { DateRange, DateTimeRangeValidationError, DesktopDateTimeRangePickerProps } from '@mui/x-date-pickers-pro'
import {
  DesktopDateTimeRangePicker,
  LocalizationProvider,
  SingleInputDateTimeRangeField,
} from '@mui/x-date-pickers-pro'
import type { DateTime } from 'luxon'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

const CUSTOM_DATE_TIME_RANGE_PICKER_WIDTH = '338px'

/**
 * It contains some defaults for the DesktopDateTimeRangePicker component, so we achieve a consistent look and feel.
 *
 */
function CustomDateTimeRangePicker({
  sx,
  slotProps,
  slots,
  value,
  onChange,
  onAccept,
  ...props
}: Readonly<DesktopDateTimeRangePickerProps>) {
  const { i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  // Internal draft state to track selection while picker is open
  const [draftValue, setDraftValue] = useState<DateRange<DateTime>>(value ?? [null, null])

  // Sync draft value with prop value when it changes externally
  useEffect(() => {
    setDraftValue(value ?? [null, null])
  }, [value])

  function handleChange(
    newValue: DateRange<DateTime>,
    context: PickerChangeHandlerContext<DateTimeRangeValidationError>,
  ) {
    setDraftValue(newValue)
    onChange?.(newValue, context)
  }

  function handleAccept(
    newValue: DateRange<DateTime>,
    context: PickerChangeHandlerContext<DateTimeRangeValidationError>,
  ) {
    onAccept?.(newValue, context)
  }

  function handleClose() {
    // Reset draft to the committed value when closing without accept
    setDraftValue(value ?? [null, null])
    setOpen(false)
  }

  return (
    <LocalizationProvider adapterLocale={i18n.language} dateAdapter={AdapterLuxon}>
      <DesktopDateTimeRangePicker
        disableFuture
        ampm={false}
        calendars={2}
        enableAccessibleFieldDOMStructure={false}
        format="dd-MM-yyyy, HH:mm"
        open={open}
        slotProps={{
          ...slotProps,
          textField: {
            ...slotProps?.textField,
            InputProps: {
              endAdornment: <EventOutlined style={{ cursor: 'pointer' }} onClick={() => setOpen((prev) => !prev)} />,
            },
          },
        }}
        slots={{ ...slots, field: SingleInputDateTimeRangeField }}
        sx={{ ...sx, minWidth: CUSTOM_DATE_TIME_RANGE_PICKER_WIDTH }}
        value={draftValue}
        onAccept={handleAccept}
        onChange={handleChange}
        onClose={handleClose}
        onOpen={() => setOpen(true)}
        {...props}
      />
    </LocalizationProvider>
  )
}

export default CustomDateTimeRangePicker
